import { useCallback, useEffect, useState } from 'react';

import {
  AcademicLevelListItem,
  CareerLevelListItem,
  SchoolsListItem,
} from '../../../../api/requests/schools';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { ModalityListItem } from '../../../../types/ModalityTypes';
import { PeriodType } from '../../../../types/PeriodType';
import { getStudyPlanFormData, getStudyPlanRequest } from '../api/study-plan';
import { StudyPlanItem } from '../interfaces/study-plan-item.interface';

export interface StudyPlanRecord {
  id: number;
  code: string;
  name: string;
  modalityId: number;
  schoolId: number;
  periodTypeId: number;
}

export type RequestType = 'HTTP_ERROR' | 'CONNECTION' | null;

export type ErrorType = {
  error: {
    code: RequestType;
  };
};

export interface ErrorsType {
  modalitiesErrorResponse: ErrorType;
  schoolsErrorResponse: ErrorType;
  studyPlanErrorResponse: ErrorType;
}

export interface RequestsData {
  studyPlan: StudyPlanItem | null;
  schools: SchoolsListItem[];
  modalities: ModalityListItem[];
  periodTypes: PeriodType[];
  academicLevels: AcademicLevelListItem[];
  careerLevels: CareerLevelListItem[];
  academicAreas: AcademicLevelListItem[];
  admissionRequirements: AcademicLevelListItem[];
}

export interface StudyPlanRecordLoaderProps {
  studyPlanId?: number;
  children: (data: RequestsData) => JSX.Element;
}

export const StudyPlanRecordLoader = (props: StudyPlanRecordLoaderProps) => {
  const { children, studyPlanId } = props;
  const [loading, setLoading] = useState(true);
  const [modalities, setModalities] = useState<ModalityListItem[]>([]);
  const [periodTypes, setPeriodTypes] = useState<PeriodType[]>([]);
  const [schools, setSchools] = useState<SchoolsListItem[]>([]);
  const [studyPlan, setStudyPlan] = useState<StudyPlanItem | null>(null);
  const [academicLevels, setAcademicLevels] = useState<AcademicLevelListItem[]>(
    [],
  );
  const [careerLevels, setCareerLevels] = useState<CareerLevelListItem[]>([]);
  const [academicAreas, setAcademicAreas] = useState<AcademicLevelListItem[]>(
    [],
  );
  const [admissionRequirements, setAdmissionRequirements] = useState<
    AcademicLevelListItem[]
  >([]);

  const [error, setError] = useState<'HTTP_ERROR' | 'CONNECTION' | null>(null);

  const loadData = useCallback(async () => {
    const { data } = await getStudyPlanFormData();

    if (data) {
      setModalities(data.modalities);
      setSchools(data.schools);
      setPeriodTypes(data.periodTypes);
      setAcademicLevels(data.academicLevels);
      setCareerLevels(data.careerLevels);
      setAcademicAreas(data.academicArea);
      setAdmissionRequirements(data.admissionRequirement);
    }

    if (studyPlanId) {
      getStudyPlanRequest(studyPlanId)
        .then((response) => {
          if (response.data) {
            setStudyPlan(response.data.data);
          } else if (response.error) {
            setError(response.error.code);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [studyPlanId]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <DisplayError textBody={error} />;
  }

  if (studyPlanId && !studyPlan) {
    return <DisplayError textBody="HTTP_ERROR" />;
  }

  return children({
    studyPlan,
    schools,
    modalities,
    periodTypes,
    academicLevels,
    careerLevels,
    academicAreas,
    admissionRequirements,
  });
};
